import React, {useState} from 'react';
import {Label, FlexRow, Modal, Text, FlexColumn, RadioGroupHandler, Frame} from '@forecast-it/design-system';
import {useIntl, FormattedMessage} from 'react-intl';
import {PERIOD_BUDGET_TYPE} from '../../../constants';
import Util from '../../../forecast-app/shared/util/util';
import DeleteRetainerPeriodMutation from '../../../mutations/delete_retainer_period_mutation';
import DeleteRetainerPeriodBulkMutation from '../../../mutations/delete_retainer_period_bulk_mutation';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';

type DeleteRetainerPeriodModalProps = {
	closeModal: () => void;
	rollovers: rolloverType[];
	periodIds: number[];
	projectId: number;
	periodBudgetType: string;
	currency: string;
};

type rolloverType = {
	id: string;
	periodFrom: {
		id: string;
		name: string;
	} | null;
	periodTo: {
		id: string;
		name: string;
	} | null;
	amount: number | null;
};

const uniquePeriodFromNames = (rollovers: rolloverType[]) => [...new Set(rollovers.map(rollover => rollover.periodFrom?.name))];

const DeleteRetainerPeriodModal = ({
	rollovers,
	periodIds,
	projectId,
	periodBudgetType,
	currency,
	closeModal,
}: DeleteRetainerPeriodModalProps): JSX.Element => {
	const [deletionConfirmed, setDeletionConfirmed] = useState(false);
	const [revertRollovers, setRevertRollovers] = useState<string>('false');
	const intl = useIntl();
	const {formatNumber} = intl;
	const hasRollovers = rollovers.length > 0;
	const subtractedRollovers = rollovers.filter(rollover => rollover.amount && rollover.amount < 0);
	const addedRollovers = rollovers.filter(rollover => rollover.amount && rollover.amount >= 0);
	const hasSubtractedRollovers = subtractedRollovers.length > 0;
	const hasAddedRollovers = addedRollovers.length > 0;
	const isFixedPrice = periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const isBulk = periodIds.length > 1;

	const saveChanges = () => {
		if (hasRollovers && !deletionConfirmed) {
			setDeletionConfirmed(true);
		} else {
			const onSuccess = () => {
				dispatch(EVENT_ID.RETAINER_UPDATE_PROJECT);
				closeModal();
			};
			const keepRollovers = revertRollovers === 'false';
			if (isBulk) {
				Util.CommitMutation(
					DeleteRetainerPeriodBulkMutation,
					{
						ids: periodIds,
						projectId,
						keepRollovers,
					},
					onSuccess
				);
			} else {
				Util.CommitMutation(
					DeleteRetainerPeriodMutation,
					{
						id: periodIds[0],
						projectId,
						keepRollovers,
					},
					onSuccess
				);
			}
		}
	};

	const formatRolloverAmount = (rollover: rolloverType) =>
		isFixedPrice
			? Util.getFormattedCurrencyValue(
					Util.GetCurrencySymbol(currency),
					formatNumber(Math.abs(rollover.amount ?? 0), {
						format: 'always_two_decimal',
					})
			  )
			: Util.convertMinutesToFullHour(Math.abs(rollover.amount ?? 0) * 60, intl);

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id={isBulk ? 'retainer.delete_periods' : 'retainer.delete_period'} />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					{deletionConfirmed ? (
						<>
							{hasAddedRollovers && (
								<FlexColumn gap={'s'}>
									<Text>
										<FormattedMessage
											id={
												isBulk
													? isFixedPrice
														? 'retainer.delete_period.add_info_bulk_price'
														: 'retainer.delete_period.add_info_bulk_hours'
													: isFixedPrice
													? 'retainer.delete_period.add_info_price'
													: 'retainer.delete_period.add_info_hours'
											}
										/>
									</Text>
									<Frame>
										<FlexColumn gap={'xxs'}>
											{isBulk
												? uniquePeriodFromNames(addedRollovers).map(name => <Text>{name}</Text>)
												: addedRollovers.map(rollover =>
														rollover.periodTo ? (
															<Text>
																{`${rollover.periodTo.name} (${formatRolloverAmount(
																	rollover
																)})`}
															</Text>
														) : null
												  )}
										</FlexColumn>
									</Frame>
								</FlexColumn>
							)}
							{hasSubtractedRollovers && (
								<FlexColumn gap={'s'}>
									<Text>
										<FormattedMessage
											id={
												isBulk
													? isFixedPrice
														? 'retainer.delete_period.subtract_info_bulk_price'
														: 'retainer.delete_period.subtract_info_bulk_hours'
													: isFixedPrice
													? 'retainer.delete_period.subtract_info_price'
													: 'retainer.delete_period.subtract_info_hours'
											}
										/>
									</Text>
									<Frame>
										<FlexColumn gap={'xxs'}>
											{isBulk
												? uniquePeriodFromNames(subtractedRollovers).map(name => <Text>{name}</Text>)
												: subtractedRollovers.map(rollover =>
														rollover.periodTo ? (
															<Text>
																{`${rollover.periodTo.name} (${formatRolloverAmount(
																	rollover
																)})`}
															</Text>
														) : null
												  )}
										</FlexColumn>
									</Frame>
								</FlexColumn>
							)}

							<FlexColumn gap={'xxs'}>
								<Label>
									<FormattedMessage
										id={
											'retainer.delete_period.confirm_remove_' +
											(hasAddedRollovers ? 'add_' : '') +
											(hasAddedRollovers && hasSubtractedRollovers ? 'and_' : '') +
											(hasSubtractedRollovers ? 'substract_' : '') +
											(isBulk && hasAddedRollovers && !hasSubtractedRollovers ? 'bulk_' : '') +
											(isFixedPrice ? 'price' : 'hours')
										}
									/>
								</Label>
							</FlexColumn>
							<RadioGroupHandler onValueChange={setRevertRollovers} value={revertRollovers}>
								<FlexRow gap={'s'}>
									<RadioGroupHandler.Item value={'true'} />
									<Text>
										<FormattedMessage
											id={
												'retainer.delete_period.remove_' +
												(hasAddedRollovers ? 'add_' : '') +
												(hasAddedRollovers && hasSubtractedRollovers ? 'and_' : '') +
												(hasSubtractedRollovers ? 'substract_' : '') +
												(isFixedPrice ? 'price' : 'hours')
											}
										/>
									</Text>
								</FlexRow>
								<FlexRow gap={'s'}>
									<RadioGroupHandler.Item value={'false'} />
									<Text>
										<FormattedMessage
											id={
												'retainer.delete_period.keep_' +
												(hasAddedRollovers ? 'add_' : '') +
												(hasAddedRollovers && hasSubtractedRollovers ? 'and_' : '') +
												(hasSubtractedRollovers ? 'substract_' : '') +
												(isFixedPrice ? 'price' : 'hours')
											}
										/>
									</Text>
								</FlexRow>
							</RadioGroupHandler>
						</>
					) : (
						<Text>
							<FormattedMessage
								id={isBulk ? 'retainer.delete_confirmation_bulk' : 'retainer.delete_confirmation'}
							/>
						</Text>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={saveChanges}
					dataCy={'modal-btn-confirm-delete'}
					disabled={deletionConfirmed && revertRollovers === null}
				>
					<FormattedMessage id="common.confirm" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default DeleteRetainerPeriodModal;
